import {
  DispatchFormDefaultValues,
  DispatchFormProps,
  DispatchFormRawValues,
  DispatchFormType
} from "./DispatchForm";
import {useTranslation} from "react-i18next";
import {useForm} from "@mantine/form";
import {getObjectId} from "@ots-frontend/ots-data";
import { useAuth } from "../../../providers/AuthProvider/AuthProvider";

const getInitialValues = (defaultValues: DispatchFormDefaultValues | undefined): DispatchFormRawValues => {
  return {
    date: defaultValues?.date ?? new Date(),
    company: getObjectId(defaultValues?.company),
    person: getObjectId(defaultValues?.person),
    vehicle: getObjectId(defaultValues?.vehicle),
    trailer: getObjectId(defaultValues?.trailer),
    quantity: defaultValues?.quantity?.number,
    quantityUom: defaultValues?.quantity?.units,
    freightType: getObjectId(defaultValues?.freightType),
    serviceType: getObjectId(defaultValues?.serviceType),
    pickup: {
      siteCompany: getObjectId(defaultValues?.pickup?.siteCompany),
      site: getObjectId(defaultValues?.pickup?.site),
      well: getObjectId(defaultValues?.pickup?.well),
      container: getObjectId(defaultValues?.pickup?.container),
    },
    delivery: {
      siteCompany: getObjectId(defaultValues?.delivery?.siteCompany),
      site: getObjectId(defaultValues?.delivery?.site),
      well: getObjectId(defaultValues?.delivery?.well),
      container: getObjectId(defaultValues?.delivery?.container),
    },
    splits: defaultValues?.splits?.map((defaultSplit) => ({
      type: defaultSplit?.type ?? "PICKUP",
      quantity: defaultSplit?.quantity?.number,
      quantityUom: defaultSplit?.quantity?.units,
      freightType: getObjectId(defaultSplit?.freightType),
      serviceType: getObjectId(defaultSplit?.serviceType),
      location: {
        siteCompany: getObjectId(defaultSplit?.location?.siteCompany),
        site: getObjectId(defaultSplit?.location?.site),
        well: getObjectId(defaultSplit?.location?.well),
        container: getObjectId(defaultSplit?.location?.container),
      },
      comments: defaultSplit?.comments,
    })),
    workOrderNumber: defaultValues?.workOrderNumber,
    comments: defaultValues?.comments,
    copyDispatchRequestComments: defaultValues?.copyDispatchRequestComments ?? false,
    carryoverExcluded: defaultValues?.carryoverExcluded ?? false,
  }
}

export type UseDispatchFormProps = Pick<DispatchFormProps, 'defaultValues'> & {}

export function useDispatchForm(props: UseDispatchFormProps): DispatchFormType {
  const {
    defaultValues
  } = props

  const {companyId} = useAuth()
  const {t} = useTranslation(["common"])

  const form = useForm({
    initialValues: getInitialValues({
      ...defaultValues,
      company: defaultValues?.company ?? (typeof defaultValues?.person === 'undefined' ? companyId : undefined),
      quantity: {
        number: defaultValues?.quantity?.number ?? 1,
        units: defaultValues?.quantity?.units ?? "loads",
      },
    }),
    validate: {
      date: (value) => value ? undefined : t("common.validation.required", "Required"),
      quantity: (value) => value ? undefined : t("common.validation.required", "Required"),
      quantityUom: (value) => value ? undefined : t("common.validation.required", "Required"),
      freightType: (value) => value ? undefined : t("common.validation.required", "Required"),
    },
  })

  return form
}
